import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Observable } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { AddProcessorComponent } from '../add-processor/add-processor.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import * as _ from 'lodash';
import { SnackbarService } from '../../services/snackbar.service';
import { take } from 'rxjs/operators';
import { ProcessorsService } from '../../services/processors.service';
import {
  EditProcessorComponent,
  ServicePermissions
} from '../edit-processor/edit-processor.component';

@Component({
  selector: 'processors-list',
  templateUrl: './processors-list.component.html',
  styleUrls: ['./processors-list.component.scss']
})
export class ProcessorsListComponent implements OnInit, OnDestroy {
  displayedColumns: string[];
  public fetchingProcessors$: Observable<any>;
  public fetchingProcessorsCount$: Observable<any>;
  public isLoading = false;
  private processorSub!: Subscription;
  private processorCountSub!: Subscription;
  activeProcessorFilter = true;
  dataSource: any;
  paginatorControl: {
    length: number;
    pageSize: number;
    pageSizeOptions: number[];
    pageIndex: number;
  };

  constructor(
    private processorsService: ProcessorsService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService
  ) {
    this.displayedColumns = ['Id', 'firstName', 'lastName', 'loginEmail', 'edit', 'active'];
    this.fetchingProcessors$ = this.processorsService.getProcessorsListUpdateListener$();
    this.fetchingProcessorsCount$ = this.processorsService.getProcessorsCountListener$();
    this.isLoading = false;
    this.dataSource = [];
    this.paginatorControl = {
      length: 100,
      pageSize: 10,
      pageSizeOptions: [5, 10, 20, 40, 50, 100],
      pageIndex: 0
    };
  }

  ngOnInit() {
    this.isLoading = true;
    // Reset to page 1 on every visit.
    this.paginatorControl.pageIndex = 0;
    this.processorsService.updateJobsListPagination({
      pageSize: this.paginatorControl.pageSize,
      pageIndex: 0
    });
    this.processorCountSub = this.fetchingProcessorsCount$.subscribe((count) => {
      if (typeof count !== 'number') {
        return;
      }

      this.paginatorControl.length = count;

      const lastPageIndex = Math.max(0, Math.ceil(count / this.paginatorControl.pageSize) - 1);
      if (this.paginatorControl.pageIndex > lastPageIndex) {
        this.paginatorControl.pageIndex = lastPageIndex;
        this.processorsService.updateJobsListPagination({
          pageSize: this.paginatorControl.pageSize,
          pageIndex: this.paginatorControl.pageIndex
        });
        this.processorsService.getProcessors(this.activeProcessorFilter);
      }
    });

    this.processorSub = this.fetchingProcessors$.subscribe((processorList) => {
      if (Array.isArray(processorList) == true) {
        this.isLoading = false;
        this.dataSource = processorList;
      }
    });

    this.processorsService.getProcessorCountData();
    this.processorsService.getProcessors();
  }

  paginate(pageEvent: PageEvent) {
    const { pageSize, pageIndex } = pageEvent;
    this.paginatorControl.pageSize = pageSize;
    this.paginatorControl.pageIndex = pageIndex;
    this.processorsService.updateJobsListPagination({ pageSize, pageIndex });
    this.isLoading = true;
    this.processorsService.getProcessors(this.activeProcessorFilter);
  }

  createProcessor() {
    const dialogConfig = new MatDialogConfig();
    const dialogRef = this.dialog.open(AddProcessorComponent, dialogConfig);
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isLoading = true;
        this.processorsService.getProcessorCountData();
        this.processorsService.getProcessors();
      });
  }

  onStateChange(event: any, processor: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      member: processor,
      action: event.checked == true ? 'Activate' : 'Archive'
    };
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    let dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((dialogResult) => {
        if (dialogResult == true) {
          const disabled = event.checked !== true;
          this.processorsService
            .setProcessorStatus({ loginEmail: processor.loginEmail, disabled })
            .subscribe(
              (responseData: any) => {
                this.snackbarService.showSnackbar(responseData.message);
                this.showProcessors(this.activeProcessorFilter);
              },
              (err) => {
                event.source.checked = event.checked !== true;
                console.error(err);
                this.snackbarService.handleError(
                  `Could not ${disabled ? 'disable' : 'enable'} processor.`
                );
              }
            );
        } else {
          event.source.checked = event.checked !== true;
        }
      });
  }

  showProcessors(isActive: boolean) {
    const isFilterChange = isActive !== this.activeProcessorFilter;
    this.activeProcessorFilter = isActive;

    if (isFilterChange) {
      this.paginatorControl.pageIndex = 0;
    }

    this.isLoading = true;

    this.processorsService.updateJobsListPagination({
      pageSize: this.paginatorControl.pageSize,
      pageIndex: this.paginatorControl.pageIndex
    });

    this.processorsService.getProcessorCountData(this.activeProcessorFilter);
    this.processorsService.getProcessors(this.activeProcessorFilter);
  }

  editProcessor(member: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = member;
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    const dialogRef = this.dialog.open(EditProcessorComponent, dialogConfig);
    dialogRef
      .afterClosed()
      .subscribe(
        ({
          servicePermissions,
          invoicingPermissions,
          costViewingPermissions
        }: {
          servicePermissions: ServicePermissions;
          invoicingPermissions: boolean;
          costViewingPermissions: boolean;
        }) => {
          if (!(_.isEmpty(servicePermissions) || _.isEmpty(invoicingPermissions))) {
            this.dataSource.find((attr: any, i: number) => {
              this.dataSource[i].servicePermissions = servicePermissions;
              this.dataSource[i].invoicingPermissions = invoicingPermissions;
              this.dataSource[i].costViewingPermissions = costViewingPermissions;
            });
          }
        }
      );
  }

  ngOnDestroy() {
    this.processorSub.unsubscribe();
    this.processorCountSub.unsubscribe();
  }
}
